import React, { useState } from "react";
import {
  Typography,
  TextField,
  Switch,
  Paper,
  FormControlLabel,
  Radio,
  FormLabel,
  RadioGroup,
  Checkbox,
  Tooltip,
  FormControl,
  InputAdornment,
} from "@mui/material";
import { createStyles, Theme } from "@mui/material/styles";
import { ICustomerIncomeDebt } from "../../../Interfaces/Customer/ICustomerIncomeDebt";
import { isPositiveNumber, isValidDate, nullOrNumber, nullOrPositiveNumber } from "../../../utils/utils";
import { ICustomerPreviousCarCredit } from "../../../Interfaces/Customer/ICustomerPreviousCarCredit";
import { Income, IncomeGrid } from "@amsanalytics/react-shared-library";
import { NamedId } from "../../../Interfaces/NamedId";
import { useDialog } from "../../../providers/DialogProvider";
import { State, orderBy } from "@progress/kendo-data-query";
import { useDispatch } from "react-redux";
import { createIncome, deleteIncome, updateIncome } from "../../../store/application/types";
import { hasValue, DatePickerWithYearSanitizer } from "@amsanalytics/react-shared-library";
import {DatePickerChangeEvent} from "@progress/kendo-react-dateinputs";
import styles from "./_income-debt.module.scss"

// const useStyles: any = makeStyles((theme: Theme) =>
//   createStyles({
//     root: {
//       display: "flex",
//       flexDirection: "column",
//       gap: '10px'
//     },
//     rootDisabled: {
//       display: "flex",
//       flexDirection: "column",
//       gap: '10px',
//       pointerEvents: 'none',
//       opacity: 0.7
//     },
//     flexRowWrap: {
//       display: "flex",
//       flexWrap: "wrap",
//       alignItems: "center",
//       gap: '10px',
//       fontSize: '0.8em',
//     },
//     flexRowWrapFullWidth: {
//       display: "flex",
//       flexWrap: "wrap",
//       gap: '10px',
//       fontSize: '0.8em',
//       width: '100%'
//     },
//     paper: {
//       padding: theme.spacing(1),
//     },
//     incomesGrid: {
//       padding: theme.spacing(1),
//     },
//     previousCarCreditContainer: {
//       padding: theme.spacing(1),
//       width: '100%'
//     }
//   })
// );

interface IncomeDebtProps {
  incomes: Income[]; // list of incomes
  incomeTypes: NamedId[]; // list of income types
  customerId: string; // customer id, used when creating and updating incomes
  clientName?: string;
  incomeDebt?: ICustomerIncomeDebt;
  previousCarCredit: ICustomerPreviousCarCredit;
  isPreviousCustomer?: boolean;
  onUpdatePreviousCarCredit(previousCarCredit: ICustomerPreviousCarCredit): void;
  onUpdateIncomeDebt(incomeDebt: ICustomerIncomeDebt): void;
  onUpdateIsPreviousCustomer(isPreviousCustomer: boolean): void;
  onUpdateIsPreviousCustomer(isPreviousCustomer: boolean): void;
  disabled?: boolean;
  isReadOnly: boolean;
}

const IncomeDebt: React.FC<IncomeDebtProps> = (props) => {
  // open and close dialog
  const [openDialog, closeDialog] = useDialog();
  // to dispatch actions
  const dispatch = useDispatch();
  // income grid state
  const [gridState, setGridState] = useState<State>({ skip: 0, take: 20, sort: [{ field: 'createdDate', dir: 'desc' }] });
  // get incomes sorted by grid state
  const getSortedIncomes = (): Income[] => {
    let sorted: Income[] = orderBy(props.incomes, gridState.sort || []);
    // if sort descrpitors include incomeTypeName, sort by name by mapping to income type name
    if (gridState.sort?.some(s => s.field === 'incomeTypeName')) {
      sorted = sorted.sort((a, b) => {
        const incomeTypeA = props.incomeTypes.find(it => it.id === a.incomeTypeId);
        const incomeTypeB = props.incomeTypes.find(it => it.id === b.incomeTypeId);
        if (incomeTypeA && incomeTypeB) {
          return gridState.sort?.find(s => s.field === 'incomeTypeName')?.dir === 'asc' ? incomeTypeA.name.localeCompare(incomeTypeB.name) : incomeTypeB.name.localeCompare(incomeTypeA.name);
        }
        return 0;
      });
    }
    // if sort descrpitors include amountFormatted, sort by the amount property
    if (gridState.sort?.some(s => s.field === 'amountFormatted')) {
      sorted = sorted.sort((a, b) => {
        return gridState.sort?.find(s => s.field === 'amountFormatted')?.dir === 'asc' ? a.amount - b.amount : b.amount - a.amount;
      });
    }
    return sorted;
  };
  const handleUpdate = <T extends {} | null>(
    value: T,
    fieldName: string
  ): void => {
    if (props.incomeDebt) {
      // Make a copy of value we are updating.
      const incomeDebt: ICustomerIncomeDebt = {
        ...props.incomeDebt
      };
      // Update property with new value.
      incomeDebt[fieldName] = value;
      props.onUpdateIncomeDebt(incomeDebt);
    }
  };
  /**
   * A customer is deemed to be making payments on a vehicle if all these fields have a value:
   * 1. Finance Company
   * 2. Vehicle Make
   * 3. Vehicle Model
   * 4. Vehicle Year
   * @returns True if all the above fields have a value, otherwise false.
   */
  const isMakingPaymentsOnVehicle = (): boolean => {
    return hasValue(props.previousCarCredit?.whoFinanced)
      && hasValue(props.previousCarCredit?.previousCarMake)
      && hasValue(props.previousCarCredit?.previousCarModel)
      && isPositiveNumber(props.previousCarCredit?.previousCarYear);
  }
  return (
    <div className={styles.incomeDebtContainer }>
      {props.incomeDebt ? (
        <div className={props.isReadOnly ? styles.rootDisabled : styles.root}>
          <Paper className={styles.paper}>
            <Typography variant="h6" component="h6">
              Income and Debt
            </Typography>

            <div className={styles.flexRowWrap}>
              <div className={styles.flexRowWrapFullWidth}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={props.incomeDebt.supportPayments}
                      onChange={(e) =>
                        handleUpdate<boolean>(
                          e.currentTarget.checked,
                          "supportPayments"
                        )
                      }
                    />
                  }
                  label="Obligated to make alimony, child support or separate maintenance payments?"
                  disabled={props.disabled}
                />
                <TextField
                  disabled={props.disabled}
                  value={props.incomeDebt.childCarePayment || ""}
                  onChange={(e) =>
                    handleUpdate<number | null>(
                      nullOrPositiveNumber(e.currentTarget.value),
                      "childCarePayment"
                    )
                  }
                  sx={{ minWidth: "200px" }}
                  type="number"
                  label="If yes, monthly payment"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </div>

              <div className={styles.flexRowWrapFullWidth}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={props.incomeDebt.bankruptcyFiled}
                      onChange={(e) =>
                        handleUpdate<boolean>(
                          e.currentTarget.checked,
                          "bankruptcyFiled"
                        )
                      }
                    />
                  }
                  label="Have you ever filed for bankruptcy?"
                  disabled={props.disabled}
                />
                <DatePickerWithYearSanitizer
                  disabled={props.disabled}
                  label="Discharge Date"
                  value={isValidDate(props.incomeDebt.dischargeDate) ? new Date(props.incomeDebt.dischargeDate!) : null}
                  onChange={(event: DatePickerChangeEvent) => handleUpdate(event.value, "dischargeDate")}
                />

                <FormControl sx={{ marginLeft: '1em' }}>
                  <FormLabel component="legend">If yes, Chapter</FormLabel>
                  <RadioGroup
                    hidden={props.disabled}
                    aria-label="position"
                    name="position"
                    value={props.incomeDebt.bankruptcyChapter?.toString() || ""}
                    onChange={(e) =>
                      handleUpdate<string>(
                        e.currentTarget.value,
                        "bankruptcyChapter"
                      )
                    }
                    row
                    style={{ alignItems: 'flex-end' }}
                  >
                    <FormControlLabel
                      value="7"
                      control={<Radio color="primary" />}
                      label="7"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="11"
                      control={<Radio color="primary" />}
                      label="11"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="13"
                      control={<Radio color="primary" />}
                      label="13"
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </FormControl>
              </div>
              <div className={styles.flexRowWrapFullWidth}>
                <FormControlLabel
                  disabled={props.disabled}
                  control={
                    <Switch
                      onChange={(e) =>
                        handleUpdate<boolean>(
                          e.currentTarget.checked,
                          "reposessed"
                        )
                      }
                      checked={props.incomeDebt.reposessed}
                    />
                  }
                  label="Have you ever had a vehicle or merchandise reposessed?"
                />

                <DatePickerWithYearSanitizer
                  disabled={props.disabled}
                  label="If yes, when?"
                  value={isValidDate(props.incomeDebt.repoDate) ? new Date(props.incomeDebt.repoDate!) : null}
                  onChange={(event: DatePickerChangeEvent) => handleUpdate(event.value, "repoDate")}
                />
                <FormControl sx={{ marginLeft: '1em' }}>
                  <FormLabel component="legend">Merchandise or Vehicle</FormLabel>
                  <RadioGroup
                    hidden={props.disabled}
                    aria-label="position"
                    name="position"
                    value={props.incomeDebt.repoType?.toString() || ""}
                    onChange={(e) =>
                      handleUpdate<number | null>(
                        nullOrNumber(e.target.value),
                        "repoType"
                      )
                    }
                    row
                  >
                    <FormControlLabel
                      value="1"
                      control={<Radio color="primary" />}
                      label="Merchandise"
                      labelPlacement="end"
                    />
                    <FormControlLabel
                      value="2"
                      control={<Radio color="primary" />}
                      label="Vehicle"
                      labelPlacement="end"
                    />
                  </RadioGroup>
                </FormControl>
              </div>
              <TextField
                disabled={props.disabled}
                style={{ minWidth: '250px' }}
                label="Prior vehicle amount borrowed?"
                type="number"
                value={props.incomeDebt.priorVehicleAmountBorrowed || ""}
                onChange={(e) =>
                  handleUpdate<number | null>(
                    nullOrNumber(e.currentTarget.value),
                    "priorVehicleAmountBorrowed"
                  )
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                disabled={props.disabled}
                value={props.incomeDebt.priorVehicleAmountOwed || ""}
                onChange={(e) =>
                  handleUpdate<number | null>(
                    nullOrNumber(e.currentTarget.value),
                    "priorVehicleAmountOwed"
                  )
                }
                label="Amount Owed?"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                disabled={props.disabled}
                value={props.incomeDebt.bankName || ""}
                onChange={(e) =>
                  handleUpdate<string>(e.currentTarget.value, "bankName")
                }
                label="Bank Name"
              />
              <TextField
                disabled={props.disabled}
                value={props.incomeDebt.bankCity || ""}
                onChange={(e) =>
                  handleUpdate<string>(e.currentTarget.value, "bankCity")
                }
                label="Bank City"
              />
              <TextField
                disabled={props.disabled}
                value={props.incomeDebt.checkingBalance || ""}
                onChange={(e) =>
                  handleUpdate<number | null>(
                    nullOrPositiveNumber(e.currentTarget.value),
                    "checkingBalance"
                  )
                }
                label="Checking Balance"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                disabled={props.disabled}
                value={props.incomeDebt.savingsBalance || ""}
                onChange={(e) =>
                  handleUpdate<number | null>(
                    nullOrPositiveNumber(e.currentTarget.value),
                    "savingsBalance"
                  )
                }
                label="Savings Balance"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                disabled={props.disabled}
                value={props.incomeDebt.utilitiesPayment || ""}
                onChange={(e) =>
                  handleUpdate<number | null>(
                    nullOrPositiveNumber(e.currentTarget.value),
                    "utilitiesPayment"
                  )
                }
                label="Utilities Payment"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                disabled={props.disabled}
                value={props.incomeDebt.childCarePayment || ""}
                onChange={(e) =>
                  handleUpdate<number | null>(
                    nullOrPositiveNumber(e.currentTarget.value),
                    "childCarePayment"
                  )
                }
                label="Child care payment"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                disabled={props.disabled}
                value={props.incomeDebt.tvpayment || ""}
                onChange={(e) =>
                  handleUpdate<number | null>(
                    nullOrPositiveNumber(e.currentTarget.value),
                    "tvpayment"
                  )
                }
                label="Cable TV Payment"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <TextField
                disabled={props.disabled}
                value={props.incomeDebt.bureauOtherDebt || ""}
                onChange={(e) =>
                  handleUpdate<number | null>(
                    nullOrPositiveNumber(e.currentTarget.value),
                    "bureauOtherDebt"
                  )
                }
                label="Bureau Other Debt"
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </div>
          </Paper>
          <Paper className={styles.incomesGrid}>
            <IncomeGrid
              incomes={getSortedIncomes()}
              incomeTypes={props.incomeTypes}
              gridState={gridState}
              itemName="Non-Employment Income"
              itemNameStyle={{ color: 'rgba(0, 0, 0, 0.87)', fontSize: '20px', fontWeight: 500 }}
              disablePagination={true}
              onDataStateChanged={(dataState: State) => setGridState(dataState)}
              onCreateIncome={(income: Income) => dispatch(createIncome({ ...income, createdDate: new Date(), ownerId: props.customerId }))}
              onUpdateIncome={(income: Income) => {
                // when updating an income: if the user enters a new amount, we want to create a new record
                dispatch(updateIncome(income, props.customerId));
              }}
              onDeleteIncome={(incomeId: string) => dispatch(deleteIncome(incomeId, props.customerId))}
              openDialog={openDialog}
              closeDialog={closeDialog}
            />
          </Paper>
          <Paper className={styles.previousCarCreditContainer}>
            <Typography variant="h6" component="h6">
              Previous Car Credit
            </Typography>
            <div className={styles.flexRowWrap}>
              <FormControlLabel
                disabled={props.disabled}
                style={{ justifyContent: 'flex-end', width: '100%', marginLeft: '0px' }}
                control={<Checkbox size='small' color='primary' />}
                label={props.clientName ? `Previous Customer of ${props.clientName}?` : `Previous Customer?`}
                labelPlacement='start'
                checked={props.isPreviousCustomer}
                onChange={(e, checked) => {
                  props.onUpdateIsPreviousCustomer(checked);
                }}
              />
              <Tooltip
                title={isMakingPaymentsOnVehicle() ? 'This box is checked because the financing company, make, model, and year fields are all completed.' : 'This box is unchecked because the financing company, make, model, and year fields are not all completed.'}
              >
                <FormControlLabel
                  style={{ justifyContent: 'flex-end', width: 'fit-content', marginLeft: '0px', marginRight: '1em' }}
                  control={<Checkbox size='small' color='primary' />}
                  label={"Do you own a vehicle in your own name that you are making payments on currently?"}
                  labelPlacement='start'
                  checked={isMakingPaymentsOnVehicle()}
                  disabled={true}
                />
              </Tooltip>
              <TextField
                disabled={props.disabled}
                value={props.previousCarCredit?.whoFinanced || ""}
                onChange={(e) => {
                  props.onUpdatePreviousCarCredit({
                    ...props.previousCarCredit,
                    whoFinanced: e.currentTarget.value
                  });
                }}
                label="Financing Company"
              />
              <TextField
                disabled={props.disabled}
                value={props.previousCarCredit?.previousCarMake || ""}
                onChange={(e) => {
                  props.onUpdatePreviousCarCredit({
                    ...props.previousCarCredit,
                    previousCarMake: e.currentTarget.value
                  });
                }}
                label="Make"
              />
              <TextField
                disabled={props.disabled}
                value={props.previousCarCredit?.previousCarModel || ""}
                onChange={(e) => {
                  props.onUpdatePreviousCarCredit({
                    ...props.previousCarCredit,
                    previousCarModel: e.currentTarget.value
                  });
                }}
                label="Model"
              />
              <TextField
                disabled={props.disabled}
                value={props.previousCarCredit?.previousCarYear || ""}
                onChange={(e) => {
                  props.onUpdatePreviousCarCredit({
                    ...props.previousCarCredit,
                    previousCarYear: +e.currentTarget.value
                  });
                }}
                label="Year"
                type="number"
              />
            </div>
          </Paper>
        </div>
      ) : null}
    </div>
  );
};

export default IncomeDebt;
